import { useEffect, useState } from 'react'

const DOC_SCROLL_LOCK_CLASS = 'ui-vv-lock'

const CSS_VAR_KEYBOARD_BOTTOM_INSET = '--ui-keyboard-bottom-inset'

/** Each frame, move this fraction of the remaining gap (higher = snappier). */
const KEYBOARD_INSET_LERP = 0.32
/** While inset is growing (keyboard opening). */
const KEYBOARD_INSET_LERP_OPEN = 0.22
/** Faster follow when the target shrinks (keyboard closing). */
const KEYBOARD_INSET_LERP_CLOSE = 0.62
/** Stop animating when within this many px of the target. */
const KEYBOARD_INSET_EPSILON = 0.55

/** Absolute ceiling (px) for Safari raw inset after trim + scale. */
const SAFARI_RAW_INSET_ABS_CAP_PX = 72
/** Fraction of innerHeight applied after trim. */
const SAFARI_INSET_MAX_FRAC_OF_INNER_H = 0.14
/**
 * Light safety margin on the geometric gap (see apply). Not 1.0 so one bad frame still caps out softly.
 */
const SAFARI_KEYBOARD_GAP_SCALE = 0.55
/**
 * Strip dead air above the keyboard; 2.8× ≈ prior 1.4× applied twice per user (“same amount lower again”).
 */
const SAFARI_COMPOSER_ESTIMATE_PX = 80
const SAFARI_DEAD_GAP_OVER_COMPOSER = 2.8
/** Less trim → slightly more bottom inset (user: ~10px too low). */
const SAFARI_INSET_TRIM_RELAX_PX = 10
const SAFARI_INSET_VISUAL_TRIM_PX = Math.max(
  0,
  Math.round(SAFARI_COMPOSER_ESTIMATE_PX * SAFARI_DEAD_GAP_OVER_COMPOSER) -
    SAFARI_INSET_TRIM_RELAX_PX
)
/** Slower chase toward rising target reduces visible “overshoot” on bad vv frames (Safari only). */
const KEYBOARD_INSET_LERP_OPEN_SAFARI = 0.14
/** When offsetTop is near zero, keyboard is gone — snap inset off (avoid lerp + stale hold fighting dismiss). */
const SAFARI_KEYBOARD_DISMISSED_OFFSET_TOP_PX = 28

/**
 * True for the system browser on iPhone/iPad (WebKit, not an embedded alternate engine).
 * Do not require the substring "Safari" in the UA — Apple has reduced UA strings; missing it
 * would misclassify stock Safari as "Chrome path" and stack env(keyboard-inset-height) + var.
 * iOS Chrome/Firefox/Edge identify with CriOS, FxiOS, EdgiOS, OPiOS.
 */
export function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS =
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints) > 1)
  if (!isIOS) return false
  if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return false
  return true
}

/**
 * Keyboard bottom inset (px) for non-Safari: max(inner−vv, inner−offsetTop−vv).
 * Safari uses inner−vv only in apply() — offsetTop is unreliable during keyboard transitions.
 */
function keyboardBottomInsetPx(innerH: number, vvHeight: number, offsetTop: number): number {
  const h = Math.max(0, Math.round(vvHeight))
  const byDiff = Math.max(0, Math.round(innerH - h))
  const ot = Math.round(offsetTop)
  const byLayout = Math.max(0, Math.round(innerH - ot - h))
  return Math.max(byDiff, byLayout)
}

/** Legacy / session vars; clear on mount and disable. */
function clearUiVvCssVars(root: HTMLElement) {
  root.style.removeProperty('--ui-vvh')
  root.style.removeProperty('--ui-vvot')
  root.style.removeProperty(CSS_VAR_KEYBOARD_BOTTOM_INSET)
}

/** Ratio of visual viewport height to inner height below which we treat the software keyboard as likely open. */
const KEYBOARD_COMPACT_RATIO = 0.88

function resetDocumentScroll() {
  window.scrollTo(0, 0)
  const docEl = document.documentElement
  const body = document.body
  docEl.scrollLeft = 0
  docEl.scrollTop = 0
  body.scrollLeft = 0
  body.scrollTop = 0
}

/**
 * Mobile: isCompact + --ui-keyboard-bottom-inset for chat column padding.
 * - iOS Chrome / Chromium: inset target = max(byDiff, byLayout); same lerp as Safari.
 * - iOS Safari: layout bottom gap (or byDiff), scale, subtract SAFARI_INSET_VISUAL_TRIM_PX (~1.4× composer
 *   estimate) for dead gap above keys, then caps + kickSmooth.
 *   Snap to 0 when raw is 0 and offsetTop is small. Skip resetDocumentScroll in apply and on focusin.
 *   Do not subscribe visualViewport ‘scroll’ to apply() on Safari — user pans change offsetTop/height and would
 *   thrash --ui-keyboard-bottom-inset. Do not add translateY(-offsetTop) on the same pane: Safari baseInset
 *   already uses offsetTop in gapBottom, so a global counter-translate double-counts (composer jumps too high).
 */
export function useUiVisualViewport(enabled: boolean): { isCompact: boolean } {
  const [isCompact, setIsCompact] = useState(false)

  useEffect(() => {
    const root = document.documentElement

    if (!enabled || typeof window === 'undefined') {
      clearUiVvCssVars(root)
      root.classList.remove(DOC_SCROLL_LOCK_CLASS)
      return
    }

    clearUiVvCssVars(root)
    root.classList.add(DOC_SCROLL_LOCK_CLASS)
    const safariInset = isIOSSafari()
    if (safariInset) root.setAttribute('data-ui-ios-safari', '1')
    else root.removeAttribute('data-ui-ios-safari')
    const vv = window.visualViewport
    let raf = 0

    let targetInsetPx = 0
    let smoothedInsetPx = 0
    let smoothRafId = 0
    const commitSmoothedInset = () => {
      root.style.setProperty(CSS_VAR_KEYBOARD_BOTTOM_INSET, `${Math.round(smoothedInsetPx)}px`)
    }

    const runSmooth = () => {
      const t = targetInsetPx
      const closing = t < smoothedInsetPx - 0.5
      const opening = t > smoothedInsetPx + 1.5
      let lerp = KEYBOARD_INSET_LERP
      if (closing) lerp = KEYBOARD_INSET_LERP_CLOSE
      else if (opening) lerp = safariInset ? KEYBOARD_INSET_LERP_OPEN_SAFARI : KEYBOARD_INSET_LERP_OPEN
      smoothedInsetPx += (t - smoothedInsetPx) * lerp
      if (Math.abs(t - smoothedInsetPx) < KEYBOARD_INSET_EPSILON) {
        smoothedInsetPx = t
      }
      commitSmoothedInset()
      if (Math.abs(t - smoothedInsetPx) > KEYBOARD_INSET_EPSILON) {
        smoothRafId = requestAnimationFrame(runSmooth)
      } else {
        smoothRafId = 0
      }
    }

    const kickSmooth = () => {
      if (smoothRafId === 0) {
        smoothRafId = requestAnimationFrame(runSmooth)
      }
    }

    const apply = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        raf = 0
        const innerH = window.innerHeight
        if (!vv) {
          targetInsetPx = 0
          smoothedInsetPx = 0
          if (smoothRafId) {
            cancelAnimationFrame(smoothRafId)
            smoothRafId = 0
          }
          commitSmoothedInset()
          setIsCompact(false)
          return
        }
        const h = vv.height
        const hRounded = Math.max(0, Math.round(h))
        const compact = hRounded < innerH * KEYBOARD_COMPACT_RATIO

        if (safariInset) {
          const ot = Math.round(vv.offsetTop)
          const byDiff = Math.max(0, Math.round(innerH - hRounded))
          // Standard “obscured strip” at bottom of layout viewport (MDN: visualViewport + layout).
          const gapBottom = Math.max(0, Math.round(innerH - ot - hRounded))
          const baseInset = ot > 10 ? gapBottom : byDiff
          let raw = Math.round(baseInset * SAFARI_KEYBOARD_GAP_SCALE)
          raw = Math.max(0, raw - SAFARI_INSET_VISUAL_TRIM_PX)
          const fracCap = Math.round(innerH * SAFARI_INSET_MAX_FRAC_OF_INNER_H)
          raw = Math.min(raw, fracCap, SAFARI_RAW_INSET_ABS_CAP_PX)

          if (raw === 0 && ot < SAFARI_KEYBOARD_DISMISSED_OFFSET_TOP_PX) {
            targetInsetPx = 0
            smoothedInsetPx = 0
            if (smoothRafId) {
              cancelAnimationFrame(smoothRafId)
              smoothRafId = 0
            }
            commitSmoothedInset()
          } else {
            targetInsetPx = raw
            kickSmooth()
          }
        } else {
          targetInsetPx = keyboardBottomInsetPx(innerH, h, vv.offsetTop)
          kickSmooth()
        }

        setIsCompact((c) => (c !== compact ? compact : c))

        if (!safariInset && (window.scrollY !== 0 || window.scrollX !== 0)) {
          resetDocumentScroll()
        }
      })
    }

    const onFocusIn = () => {
      if (!safariInset) {
        resetDocumentScroll()
        requestAnimationFrame(() => resetDocumentScroll())
      }
    }

    /** Safari: vv resize can lag on blur — drop target immediately when leaving editable fields. */
    const onFocusOut = () => {
      queueMicrotask(() => {
        const el = document.activeElement
        const stillEditing =
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          el instanceof HTMLSelectElement ||
          (el instanceof HTMLElement && el.isContentEditable)
        if (!stillEditing) {
          targetInsetPx = 0
          smoothedInsetPx = 0
          if (smoothRafId) {
            cancelAnimationFrame(smoothRafId)
            smoothRafId = 0
          }
          commitSmoothedInset()
        }
      })
    }

    apply()
    vv?.addEventListener('resize', apply)
    if (!safariInset) {
      vv?.addEventListener('scroll', apply)
    }
    window.addEventListener('resize', apply)
    window.addEventListener('focusin', onFocusIn, true)
    window.addEventListener('focusout', onFocusOut, true)

    return () => {
      if (raf) cancelAnimationFrame(raf)
      if (smoothRafId) cancelAnimationFrame(smoothRafId)
      vv?.removeEventListener('resize', apply)
      if (!safariInset) {
        vv?.removeEventListener('scroll', apply)
      }
      window.removeEventListener('resize', apply)
      window.removeEventListener('focusin', onFocusIn, true)
      window.removeEventListener('focusout', onFocusOut, true)
      clearUiVvCssVars(root)
      root.classList.remove(DOC_SCROLL_LOCK_CLASS)
      root.removeAttribute('data-ui-ios-safari')
      setIsCompact(false)
    }
  }, [enabled])

  return { isCompact }
}
